// The upgrade step: register → "your free seat is saved — want more?" →
// thank-you → survey (NA Summit, lane mct_f178548d-7a4; design approved by
// Michael 18 Oct 2026, mca_02efe1b70c08, mcd_73a93152-d7a; plan
// docs/plans/summit-upgrade-step-and-survey-thank-you.md).
//
// ★ Why this exists: the screen after registering did three jobs at once. It
// said "You're in", sold All Access / VIP in a full price table, and only then
// offered the prep survey, so on a phone the survey button sat under an
// upsell. The upsell and the survey got in each other's way. Now the offer is
// its own short stop with one clear yes per pass and a real "No thank you"
// button, and the thank-you leads with the survey.
//
// ★ Only for a seat that is ACTUALLY saved — the same reasoning as
// stepTwo.ts. A resume-token arrival in 'registered-pending-survey' has a
// submission row; the in-page email gate saves nothing until the survey is
// submitted, so it never sees "your free seat is saved".
//
// ★ Never for someone already on a paid tier. tierKey comes from the resume
// response (worker/src/resume-tier.ts): member auto-VIP, a referral grant and
// a Spiffy purchase all write tier_key onto the registration row, so a buyer
// back from checkout goes straight to the thank-you, never back to the offer.
import { interpolateBlocks } from './interpolate.ts';
import type { ContentBlock, GatePage, RegistrationStatus } from '../types';

export const UPGRADE_STEP_DEFAULTS = {
  seatSavedLine: 'Your free seat is saved{{greeting}}',
  declineLabel: 'No thank you, Live Access is all I need',
} as const;

/** Tier keys that mean "no paid pass yet". */
const FREE_TIER_KEYS = new Set(['', 'live', 'free']);

export interface UpgradeStepInput {
  gatePage: GatePage | undefined;
  status: RegistrationStatus | null | undefined;
  tierKey: string | null | undefined;
  memberVip?: boolean;
  /** "No thank you" already tapped for this registration in this browser. */
  declined?: boolean;
}

/** Does this visitor see the upgrade step before the thank-you? */
export function upgradeStepApplies(i: UpgradeStepInput): boolean {
  if (!i.gatePage?.upgradeStep) return false;
  if (i.status !== 'registered-pending-survey') return false;
  if (i.memberVip) return false;
  if (!FREE_TIER_KEYS.has(i.tierKey ?? '')) return false;
  if (i.declined) return false;
  return true;
}

// ---- "No thank you" memory (sessionStorage, per registration) ----
// A failed read means the step shows again, which is harmless: their seat is
// already saved and "No thank you" is one tap.

export const declineKey = (submissionId: string) => `upgrade-declined:${submissionId}`;

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'>;

export function readDeclined(store: KeyValueStore | null | undefined, submissionId: string | null | undefined): boolean {
  if (!store || !submissionId) return false;
  try { return store.getItem(declineKey(submissionId)) === '1'; } catch { return false; }
}

export function writeDeclined(store: KeyValueStore | null | undefined, submissionId: string | null | undefined): void {
  if (!store || !submissionId) return;
  try { store.setItem(declineKey(submissionId), '1'); } catch { /* private mode / blocked */ }
}

// ---- Checkout links ----

/** Add the person's email and first name to an Engine order-page link
 *  (/checkout/…) so the order page skips its email-capture step. The order
 *  page's own member check still runs. External links are left alone. */
export function withBuyerPrefill(url: string, email: string | null | undefined, firstName: string | null | undefined): string {
  if (!url.startsWith('/checkout/')) return url;
  const hashAt = url.indexOf('#');
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  const base = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const qAt = base.indexOf('?');
  const path = qAt >= 0 ? base.slice(0, qAt) : base;
  const params = new URLSearchParams(qAt >= 0 ? base.slice(qAt + 1) : '');
  const e = (email ?? '').trim();
  const f = (firstName ?? '').trim();
  if (e) params.set('email', e);
  if (f) params.set('first_name', f);
  const qs = params.toString();
  return path + (qs ? `?${qs}` : '') + hash;
}

/** Apply withBuyerPrefill to every tier CTA of every pricingTiers block. */
export function prefillTierCtas(blocks: ContentBlock[], email: string | null | undefined, firstName: string | null | undefined): ContentBlock[] {
  return blocks.map((b) => {
    if (b.type !== 'pricingTiers') return b;
    const tiers = (b as unknown as { tiers?: Array<Record<string, unknown>> }).tiers;
    if (!Array.isArray(tiers)) return b;
    return {
      ...b,
      tiers: tiers.map((t) => (typeof t.ctaUrl === 'string' ? { ...t, ctaUrl: withBuyerPrefill(t.ctaUrl, email, firstName) } : t)),
    } as unknown as ContentBlock;
  });
}

// ---- Copy ----

export interface UpgradeStepCopy {
  seatSaved: string;
  heading: string;
  body: string | null;
  blocks: ContentBlock[];
  declineLabel: string;
  footnote: string | null;
}

export function resolveUpgradeStepCopy(gatePage: GatePage, vars: Record<string, string>): UpgradeStepCopy | null {
  const u = gatePage.upgradeStep;
  if (!u) return null;
  const fill = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');
  return {
    seatSaved: fill(u.seatSavedLine ?? UPGRADE_STEP_DEFAULTS.seatSavedLine),
    heading: fill(u.heading),
    body: u.body ? fill(u.body) : null,
    blocks: interpolateBlocks(u.blocks ?? [], vars),
    declineLabel: fill(u.declineLabel ?? UPGRADE_STEP_DEFAULTS.declineLabel),
    footnote: u.footnote ? fill(u.footnote) : null,
  };
}

/** The thank-you's "Your VIP pass is confirmed" line — buyers only. Member
 *  auto-VIP and referral grants get none: their pass is explained elsewhere,
 *  and "confirmed" would read as if they had paid. */
export function resolveTierLine(
  gatePage: GatePage | undefined,
  tierKey: string | null | undefined,
  tierSource: string | null | undefined,
): string | null {
  if (!gatePage?.tierLines || !tierKey || tierSource !== 'purchase') return null;
  const line = gatePage.tierLines[tierKey];
  return typeof line === 'string' && line.trim() ? line : null;
}
